/**
 * Yazma kutusundaki metin — konuşma başına, React durumunun DIŞINDA.
 *
 * NEDEN (denetim Sohbet 15, ölçüldü). Taslaklar `useChatThread` içinde `useState` idi ve kanca
 * sayfanın (ya da yuvanın) tepesinde çağrılıyor. Her tuş o durumu değiştirdiği için her tuşta
 * SAYFANIN TAMAMI yeniden çiziliyordu: 300 mesajlık bir sohbette beş tuş = konuşma listesi 5,
 * mesaj paneli 5 kez; jsdom'da 84 ms. Metin artık bu küçük depoda; yalnız yazma kutusu ona abone
 * (`useSyncExternalStore`), yani tuş yalnız yazma kutusunu çiziyor.
 *
 * KONUŞMA BAŞINA, çünkü tek kanca örneği rayın ulaştığı her konuşmaya hizmet ediyor: tek bir
 * düz metin, Ayşe'ye yazılanı Mehmet'in konuşmasına taşırdı. Geri dönünce yarım metin yerinde.
 *
 * SAYFA YENİLENİNCE DE YERİNDE (sohbet-duzeltme 20 Ekim 2026, denetim H10 — ölçüldü: yarım mesaj
 * yenilemede kayboluyordu). Bellekteki harita artık `sessionStorage`'ın önünde bir önbellek:
 *  - `sessionStorage`, `localStorage` DEĞİL: bu sekmenin kendi deposu, sekme kapanınca gidiyor —
 *    taslak kişisel içerik ve paylaşılan bir depo bilgisayarında ertesi güne kalmamalı;
 *  - anahtar KİŞİYİ de taşıyor (`altek.chat.draft:<kişi>:<konuşma>`): aynı sekmede başka biri
 *    oturum açarsa öncekinin taslağını görmez, ve depo açılırken başka kişinin anahtarları siliniyor;
 *  - «Çıkış yap» bütün sohbet taslaklarını siliyor (`forgetChatDrafts`, `useSignOut`);
 *  - depo erişimi her yerde `try` içinde: gizli pencere ya da kota hatası taslağı belleğe düşürür,
 *    yazma kutusunu bozmaz.
 */
export interface DraftStore {
  get: (conversationId: string) => string;
  set: (conversationId: string, text: string) => void;
  subscribe: (listener: () => void) => () => void;
}

export const DRAFT_STORAGE_PREFIX = "altek.chat.draft:";

function sessionDrafts(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function storageKey(userId: string, conversationId: string): string {
  return `${DRAFT_STORAGE_PREFIX}${userId}:${conversationId}`;
}

/** Every chat draft key in this storage — read up front, because removing while walking shifts the index. */
function draftKeys(storage: Storage): string[] {
  const keys: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(DRAFT_STORAGE_PREFIX)) keys.push(key);
    }
  } catch {
    return [];
  }
  return keys;
}

function removeKeys(storage: Storage, keys: readonly string[]): void {
  for (const key of keys) {
    try {
      storage.removeItem(key);
    } catch {
      // Silinemeyen taslak sekmeyle birlikte gidiyor.
    }
  }
}

/** «Çıkış yap»: bu sekmedeki BÜTÜN sohbet taslakları — kimin olursa olsun — siliniyor. */
export function forgetChatDrafts(storage: Storage | null = sessionDrafts()): void {
  if (!storage) return;
  removeKeys(storage, draftKeys(storage));
}

export function createDraftStore(options: { userId?: string | null; storage?: Storage | null } = {}): DraftStore {
  const drafts = new Map<string, string>();
  const listeners = new Set<() => void>();
  const userId = options.userId ? options.userId : null;
  // Kimliği bilinmeyen bir depo yalnız bellekte tutuyor: kimin taslağı olduğu yazılamazsa saklanmaz.
  const storage = userId ? (options.storage !== undefined ? options.storage : sessionDrafts()) : null;
  if (storage && userId) {
    const mine = `${DRAFT_STORAGE_PREFIX}${userId}:`;
    removeKeys(storage, draftKeys(storage).filter((key) => !key.startsWith(mine)));
  }

  const read = (conversationId: string): string => {
    const cached = drafts.get(conversationId);
    if (cached !== undefined) return cached;
    let stored = "";
    if (storage && userId) {
      try {
        stored = storage.getItem(storageKey(userId, conversationId)) ?? "";
      } catch {
        stored = "";
      }
    }
    drafts.set(conversationId, stored);
    return stored;
  };

  return {
    get: read,
    set: (conversationId, text) => {
      if (read(conversationId) === text) return;
      drafts.set(conversationId, text);
      if (storage && userId) {
        try {
          if (text === "") storage.removeItem(storageKey(userId, conversationId));
          else storage.setItem(storageKey(userId, conversationId), text);
        } catch {
          // Kota ya da gizli pencere: taslak bellekte kalıyor, yenilemede kaybolur.
        }
      }
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
